import {
  Box,
  InputAdornment,
  OutlinedInput,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import update from "immutability-helper";
import ascendingIcon from "../../assets/images/Icons/ascending.png";
import descendingIcon from "../../assets/images/Icons/descending.png";
import emptyIcon from "../../assets/images/Icons/search-table.png";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useState } from "react";
import { Loader } from "./Loader";
import { useSortable } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const DraggableTable = (props) => {
  const {
    defaultData,
    columns,
    headerText,
    rightContent,
    tableHeaderContent = null,
    isHeaderStyleChange = false,
    isLoading = false,
    isPagination = true,
    isTableHeader = true,
    isTableHead = true,
    tableFooter,
    tableStyle,
  } = props;
  const [data, setData] = React.useState([
    {
      id: "1",
      step: "aa",
    },
    {
      id: "2",
      step: "bb",
    },
    {
      id: "3",
      step: "bb",
    },
  ]);

  const dataIds = React.useMemo(() => data?.map(({ id }) => id), [data]);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);
      const dragRecord = data[oldIndex];
      const updatedRecords = update(data, {
        $splice: [
          [oldIndex, 1],
          [newIndex, 0, dragRecord],
        ],
      });
      setData(updatedRecords);
    }
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  const [state, setState] = useState(table.initialState);
  table.setOptions((prev) => ({
    ...prev,
    state,
    onStateChange: setState,
  }));

  return (
    <>
      <Stack
        sx={{
          borderRadius: "4px",
          bgcolor: "#fff",
          overflow: "hidden",
          ...tableStyle,
        }}
      >
        {isTableHeader && (
          <TableHeader
            setGlobalFilter={(value) =>
              setState({ ...state, globalFilter: value })
            }
            headerText={headerText}
            rightContent={rightContent}
            isHeaderStyleChange={isHeaderStyleChange}
          />
        )}
        {tableHeaderContent && (
          <TableHeaderContent tableHeaderContent={tableHeaderContent} />
        )}
        {isLoading ? (
          <Box sx={{ padding: "10px" }}>
            <Loader />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: "0 0 4px 4px" }}
          >
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              <Table
                sx={{
                  borderCollapse: "inherit",
                  overflow: "hidden",
                }}
              >
                {isTableHead && (
                  <TableHead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <>
                        <TableRow key={headerGroup.id}>
                          <TableCell />
                          {headerGroup.headers.map((header) => {
                            return (
                              <TableCell
                                key={header.id}
                                colSpan={header.colSpan}
                                align={"right"}
                                sx={{
                                  padding: "10px 8px",
                                  backgroundColor: "#F5F8FC",
                                }}
                              >
                                {header.isPlaceholder ? null : (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      justifyContent:
                                        header?.column?.columnDef?.align ===
                                        "right" && "flex-end",
                                    }}
                                    {...{
                                      className: header.column.getCanSort()
                                        ? "cursor-pointer select-none"
                                        : "",
                                      onClick:
                                        header.column.getToggleSortingHandler(),
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        lineHeight: "14px",
                                      }}
                                    >
                                      {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                      )}
                                    </Typography>

                                    {{
                                      asc: (
                                        <img
                                          alt="asc"
                                          src={ascendingIcon}
                                          width={14}
                                          height={14}
                                        />
                                      ),
                                      desc: (
                                        <img
                                          alt="desc"
                                          src={descendingIcon}
                                          width={14}
                                          height={14}
                                        />
                                      ),
                                    }[header.column.getIsSorted()] ?? null}
                                  </Box>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </>
                    ))}
                  </TableHead>
                )}

                <TableBody>
                  {table.getFilteredRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row) => {
                      return (
                        <SortableContext
                          items={dataIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <DraggableRow row={row} />
                        </SortableContext>
                      );
                    })
                  ) : (
                    <EmptyComponent />
                  )}
                </TableBody>
              </Table>
            </DndContext>
            {isPagination && table.getFilteredRowModel().rows.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[8, 10, 20, 30, 40, 50]}
                component="div"
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={table.getState().pagination.pageSize}
                page={table.getState().pagination.pageIndex}
                onPageChange={(e, page) => {
                  table.setPageIndex(page);
                }}
                onRowsPerPageChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
              />
            )}

            {tableFooter && <>{tableFooter}</>}
          </TableContainer>
        )}
      </Stack>
    </>
  );
};

const TableHeader = ({
  setGlobalFilter,
  headerText,
  rightContent,
  isHeaderStyleChange,
}) => {
  return (
    <Stack
      sx={{
        padding: "16px 12px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        bgcolor: isHeaderStyleChange && "#111C2B",
        color: isHeaderStyleChange && "#fff",
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "18px",
        }}
      >
        {headerText}
      </Typography>
      <OutlinedInput
        onChange={(e) => setGlobalFilter(e.target.value)}
        sx={{
          height: "36px",
          borderRadius: "10px",
          width: "200px",
          backgroundColor: isHeaderStyleChange ? "#313C4C" : "#F2F7FC",
          borderColor: isHeaderStyleChange && "#485364",
          "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: isHeaderStyleChange && "#485364",
          },
          "& input::placeholder": {
            color: isHeaderStyleChange && "#ccc",
          },
          "& input": {
            p: " 12.5px 14px 12.5px 4px !important",
          },
        }}
        placeholder="Search"
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon size="small" />
          </InputAdornment>
        }
      />
      {rightContent}
    </Stack>
  );
};

const EmptyComponent = () => {
  return (
    <TableRow>
      <TableCell
        align="center"
        colSpan={20}
        height={162}
        sx={{ py: 2, padding: "32px 0" }}
      >
        <Stack alignItems="center" gap="8px">
          <img src={emptyIcon} alt="empty" height={56} width={56} />
          <Typography
            sx={{ fontWeight: 400, fontSize: "14px", color: "#485364" }}
          >
            No matches found
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const TableHeaderContent = ({ tableHeaderContent }) => {
  return <Box p="8px 16px">{tableHeaderContent}</Box>;
};
const RowDragHandleCell = ({ rowId }) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });
  return (
    <div {...attributes} {...listeners}>
      <DragIndicatorIcon />
    </div>
    // Alternatively, you could set these attributes on the rows themselves
  );
};
const DraggableRow = ({ row }) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.original.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform), //let dnd-kit do its thing
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
    width: "100px",
  };
  return (
    // connect row ref to dnd-kit, apply important styles
    <TableRow ref={setNodeRef} style={style}>
      <TableCell
        padding={"normal"}
        sx={{
          p: "2px",
        }}
      >
        <RowDragHandleCell rowId={row.original.id} />
      </TableCell>

      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id} style={{ style }}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
};
